'use client';

import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { Typography } from '../typography/Typography';
import { useMenuItemContext } from './internal/MenuItemContext';

const menuItemAffixClasses = { root: 'zds-menu-item-affix-root' } as const;

const menuItemAffixStyles = tv({
  base: [
    menuItemAffixClasses.root,
    'flex items-center first:mr-2 last:ml-2 last:justify-end',
    '[&_svg]:h-5 [&_svg]:w-auto',
    'text-primary',
  ],
  variants: {
    isDisabled: {
      // Opacity + grayscale approximate a disabled tone for any affix content
      // (icons, Flag, Avatar images/initials) that may not inherit currentColor.
      true: 'opacity-disabled [&>*]:grayscale',
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface MenuItemAffixBaseProps {}

type MenuItemAffixProps = ComponentProps<MenuItemAffixBaseProps, 'span'>;

type MenuItemAffixComponent = Component<MenuItemAffixBaseProps, 'span'>;

const MenuItemAffix: MenuItemAffixComponent = (props) => {
  const { children, className, ref, ...rest } = props;

  const itemContext = useMenuItemContext();
  const isDisabled = itemContext?.isDisabled ?? false;

  return (
    <Typography
      {...rest}
      ref={ref}
      component="span"
      variant="body2"
      color="secondary"
      className={menuItemAffixStyles({ isDisabled, className })}
    >
      {children}
    </Typography>
  );
};

MenuItemAffix.displayName = 'Menu.ItemAffix';

export {
  MenuItemAffix,
  type MenuItemAffixProps,
  type MenuItemAffixBaseProps,
  menuItemAffixClasses,
};
